import type { DriverStop } from '../api/types';
import { copy } from '../copy';
import { signal } from '../lib/feedback';
import { classifyDeliverScan, classifyLoadScan } from '../lib/parcel';
import { formatClock } from '../lib/time';
import { useAppStore, type ScanOutcome } from './store';

function cityOf(stop: DriverStop): string {
  return stop.postalCodeCity.split(' ').slice(2).join(' ') || stop.postalCodeCity;
}

function pendingParcelsAfterDelivery(stop: DriverStop, deliveredNumber: string): number {
  return stop.parcels.filter(
    (parcel) => parcel.parcelNumber !== deliveredNumber && parcel.status !== 'delivered',
  ).length;
}

/**
 * Verwerkt één gelezen of overgetypte code. Legt de handeling onmiddellijk lokaal vast
 * (offline-first) en levert de uitkomst die scherm F2–F6 toont.
 */
export async function handleScannedCode(code: string): Promise<ScanOutcome> {
  const state = useAppStore.getState();
  const route = state.route;
  const sound = state.prefs.soundEnabled;
  const clock = formatClock(Date.now());

  if (!route) {
    signal('reject', sound);
    return {
      kind: 'unknown',
      code,
      stopId: null,
      parcelNumber: null,
      partnerName: null,
      city: null,
      clock,
      clientActionId: null,
      moreParcelsOnStop: false,
    };
  }

  if (state.scanPurpose === 'load') {
    const outcome = classifyLoadScan(route.stops, code);
    if (outcome.kind === 'unknown') {
      signal('reject', sound);
      return {
        kind: 'unknown',
        code: outcome.code,
        stopId: null,
        parcelNumber: null,
        partnerName: null,
        city: null,
        clock,
        clientActionId: null,
        moreParcelsOnStop: false,
      };
    }
    if (outcome.kind === 'alreadyLoaded') {
      signal('ok', sound);
      return {
        kind: 'alreadyLoaded',
        code: outcome.parcel.parcelNumber,
        stopId: outcome.stop.id,
        parcelNumber: outcome.parcel.parcelNumber,
        partnerName: outcome.stop.partnerName,
        city: cityOf(outcome.stop),
        clock,
        clientActionId: null,
        moreParcelsOnStop: false,
      };
    }
    const item = await state.recordAction({
      type: 'parcel.load',
      request: { parcelNumber: outcome.parcel.parcelNumber },
      subject: outcome.stop.partnerName,
    });
    signal('ok', sound);
    return {
      kind: 'loaded',
      code: outcome.parcel.parcelNumber,
      stopId: outcome.stop.id,
      parcelNumber: outcome.parcel.parcelNumber,
      partnerName: outcome.stop.partnerName,
      city: cityOf(outcome.stop),
      clock,
      clientActionId: item.clientActionId,
      moreParcelsOnStop: false,
    };
  }

  const outcome = classifyDeliverScan(route.stops, code, state.stopId);

  if (outcome.kind === 'unknown') {
    signal('reject', sound);
    return {
      kind: 'unknown',
      code: outcome.code,
      stopId: null,
      parcelNumber: null,
      partnerName: null,
      city: null,
      clock,
      clientActionId: null,
      moreParcelsOnStop: false,
    };
  }

  if (outcome.kind === 'alreadyDelivered') {
    signal('ok', sound);
    return {
      kind: 'alreadyDelivered',
      code: outcome.parcel.parcelNumber,
      stopId: outcome.stop.id,
      parcelNumber: outcome.parcel.parcelNumber,
      partnerName: outcome.stop.partnerName,
      city: cityOf(outcome.stop),
      clock: formatClock(outcome.stop.deliveredAt ?? Date.now()),
      clientActionId: null,
      moreParcelsOnStop: false,
    };
  }

  if (outcome.kind === 'otherStop') {
    signal('warn', sound);
    return {
      kind: 'otherStop',
      code: outcome.parcel.parcelNumber,
      stopId: outcome.stop.id,
      parcelNumber: outcome.parcel.parcelNumber,
      partnerName: outcome.stop.partnerName,
      city: cityOf(outcome.stop),
      clock,
      clientActionId: null,
      moreParcelsOnStop: false,
    };
  }

  return deliverParcel(outcome.stop, outcome.parcel.parcelNumber);
}

/** Meldt één pakket af — ook buiten volgorde, na bevestiging op F4 (B14). */
export async function deliverParcel(stop: DriverStop, parcelNumber: string): Promise<ScanOutcome> {
  const state = useAppStore.getState();
  const item = await state.recordAction({
    type: 'stop.deliver',
    request: { stopId: stop.id, parcelNumber },
    subject: stop.partnerName,
  });
  signal('ok', state.prefs.soundEnabled);
  return {
    kind: 'delivered',
    code: parcelNumber,
    stopId: stop.id,
    parcelNumber,
    partnerName: stop.partnerName,
    city: cityOf(stop),
    clock: formatClock(Date.now()),
    clientActionId: item.clientActionId,
    moreParcelsOnStop: pendingParcelsAfterDelivery(stop, parcelNumber) > 0,
  };
}

/** De inline foutregel van G2 bij handmatige invoer. */
export function manualErrorFor(outcome: ScanOutcome): string | null {
  switch (outcome.kind) {
    case 'unknown':
      return copy.manual.notInRoute;
    case 'alreadyDelivered':
      return copy.manual.alreadyDelivered(outcome.clock);
    case 'alreadyLoaded':
      return copy.load.alreadyLoaded;
    default:
      return null;
  }
}
